import { gql } from '@apollo/client';
import { OPERATING_HOURS_FIELDS } from '../operatingHours/queries';

/**
 * Guest catalog picker (home / search / view-car hub list).
 * Address + geo only — do not select cars, owner, or operatingHours.
 * Time slots load OH via operatingHoursByLocationAndType after a hub is chosen.
 */
export const GET_LOCATIONS = gql`
  query GetLocations {
    locations {
      id
      name
      address
      city
      state
      zipCode
      timezone
      latitude
      longitude
      taxRate
      locationType
      airportCode
    }
  }
`;

/**
 * Legacy full location — still used by some admin/tools screens.
 * Add-car / VIN must use {@link GET_LOCATION_LEAN} (no nested `cars[]`).
 */
export const GET_LOCATION = gql`
  ${OPERATING_HOURS_FIELDS}
  query GetLocation($id: Int!) {
    location(id: $id) {
      id
      name
      address
      city
      state
      zipCode
      timezone
      latitude
      longitude
      taxRate
      phoneNumber
      description
      userId
      operatingHours {
        ...OperatingHoursFields
      }
      user {
        id
        email
        firstName
        lastName
        displayName
      }
      cars {
        id
        name
        model
        make
      }
    }
  }
`;

/** Add-car OH gate + VIN ownership — slim OH only; never request nested cars. */
export const GET_LOCATION_LEAN = gql`
  query GetLocationLean($id: Int!) {
    location(id: $id) {
      id
      name
      address
      city
      state
      zipCode
      timezone
      latitude
      longitude
      userId
      operatingHours {
        id
        PickupLocationType
        alwaysAvailable
      }
    }
  }
`;

export const GET_MY_LOCATIONS = gql`
  ${OPERATING_HOURS_FIELDS}
  query GetMyLocations {
    myLocations {
      id
      name
      address
      city
      state
      zipCode
      timezone
      latitude
      longitude
      taxRate
      phoneNumber
      description
      userId
      operatingHours {
        ...OperatingHoursFields
      }
      cars {
        id
        name
        model
        make
      }
    }
  }
`;

/** Manage Locations list — address + slim OH; no nested cars[]. */
export const GET_MY_LOCATIONS_V2 = gql`
  query GetMyLocationsV2($input: GetMyLocationsV2Input!) {
    getMyLocationsV2(input: $input) {
      totalCount
      hasMore
      page
      limit
      totalPages
      items {
        id
        name
        address
        city
        state
        zipCode
        timezone
        latitude
        longitude
        taxRate
        phoneNumber
        description
        userId
        operatingHours {
          id
          PickupLocationType
          alwaysAvailable
        }
      }
    }
  }
`;

/** Add-car / peer pickers — lean fields only. */
export const GET_MY_LOCATIONS_V2_LEAN = gql`
  query GetMyLocationsV2Lean($input: GetMyLocationsV2Input!) {
    getMyLocationsV2(input: $input) {
      totalCount
      hasMore
      page
      limit
      totalPages
      items {
        id
        name
        address
        city
        state
        zipCode
        timezone
        latitude
        longitude
      }
    }
  }
`;

export const GET_PUBLIC_LOCATIONS = gql`
  query GetPublicLocations($type: LocationType, $allAirports: Boolean) {
    publicLocations(type: $type, allAirports: $allAirports) {
      id
      name
      address
      city
      state
      zipCode
      timezone
      latitude
      longitude
      taxRate
      phoneNumber
      description
      locationType
      isPublic
      isActive
      airportCode
      enabledOnBrand
    }
  }
`;

/** D38: turn an airport on or off for the viewing brand. */
export const SET_BRAND_AIRPORT_ENABLED = gql`
  mutation SetBrandAirportEnabled($locationId: Int!, $enabled: Boolean!) {
    setBrandAirportEnabled(locationId: $locationId, enabled: $enabled) {
      id
      enabledOnBrand
    }
  }
`;

/** D38: platform owner only. */
export const UPDATE_AIRPORT = gql`
  mutation UpdateAirport($input: UpdateAirportInput!) {
    updateAirport(input: $input) {
      id
      name
      address
      city
      state
      zipCode
      latitude
      longitude
      airportCode
      isActive
    }
  }
`;

/** D38: platform owner only. Never deleted; off hides it from pickup lists. */
export const SET_AIRPORT_ACTIVE = gql`
  mutation SetAirportActive($locationId: Int!, $isActive: Boolean!) {
    setAirportActive(locationId: $locationId, isActive: $isActive) {
      id
      isActive
    }
  }
`;

// Mutations
export const CREATE_LOCATION = gql`
  mutation CreateLocation($createLocationInput: CreateLocationInput!) {
    createLocation(createLocationInput: $createLocationInput) {
      id
      name
      address
      city
      state
      zipCode
      timezone
      latitude
      longitude
      taxRate
      phoneNumber
      description
      userId
    }
  }
`;

export const CREATE_PUBLIC_LOCATION = gql`
  mutation CreatePublicLocation($input: CreatePublicLocationInput!) {
    createPublicLocation(input: $input) {
      id
      name
      address
      city
      state
      zipCode
      timezone
      latitude
      longitude
      taxRate
      phoneNumber
      description
      locationType
      isPublic
      isActive
      airportCode
      enabledOnBrand
    }
  }
`;

export const UPDATE_LOCATION = gql`
  mutation UpdateLocation($id: Int!, $input: UpdateLocationInput!) {
    updateLocation(id: $id, input: $input) {
      name
      address
      city
      state
      zipCode
      timezone
      latitude
      longitude
      taxRate
      phoneNumber
      description
      userId
    }
  }
`;

export const REMOVE_LOCATION = gql`
  mutation RemoveLocation($id: Int!) {
    removeLocation(id: $id) {
      id
      success
      message
    }
  }
`;

export const GET_LOCATIONS_BY_HOST_ID = gql`
  query GetLocationsByHostId($hostId: String!) {
    locationsByHostId(hostId: $hostId) {
      id
      name
      address
      city
      state
      zipCode
      timezone
      latitude
      longitude
      taxRate
      phoneNumber
      description
      user {
        id
        firstName
        lastName
        displayName
      }
      cars {
        id
        make
        model
        name
      }
    }
  }
`;

export const GET_ALLOWED_LOCATIONS = gql`
  query GetAllowedLocations($locationId: Int!) {
    getAllowedLocations(locationId: $locationId) {
      id
      fromLocation {
        id
        name
        address
        city
        state
        zipCode
        timezone
        latitude
        longitude
      }
      toLocation {
        id
        name
        address
        city
        state
        zipCode
        timezone
        latitude
        longitude
        taxRate
        locationType
        airportCode
        phoneNumber
        description
        userId
      }
      fee
      isActive
    }
  }
`;

export const UPDATE_ALLOWED_LOCATION = gql`
  mutation SetAllowedLocation($input: AllowedLocationInput!) {
    setAllowedLocation(input: $input) {
      id
      fromLocationId
      toLocationId
      fee
      isActive
    }
  }
`;
